import { lazy, type ComponentType } from 'react';

type Loader<T extends ComponentType<any>> = () => Promise<{ default: T }>;

// A route chunk is fetched the first time that route renders. On the links
// this app is actually used on, that request does not always fail cleanly —
// it stalls, the socket gives up, and the dynamic import rejects. React's
// answer to a rejected lazy import is to throw to the nearest error
// boundary, which for a route means the operator loses the page they were
// opening and has to reload the whole app (10 MB on the old bundle, and
// still a full shell today).
//
// A dropped packet is not a missing chunk, so the import is retried before
// it is allowed to fail. Webpack clears its installed-chunk bookkeeping when
// a chunk load errors, so re-calling the same import genuinely re-requests
// the file rather than replaying the cached rejection. Delays climb so a
// link that is congested rather than dead gets a quiet moment to recover;
// after the last one the error propagates to the error boundary, which
// already offers "Try again" and "Reload page".
const RETRY_DELAYS_MS = [600, 1800, 5000];

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const loadWithRetry = async <T extends ComponentType<any>>(
  load: Loader<T>,
  attempt = 0
): Promise<{ default: T }> => {
  try {
    return await load();
  } catch (error) {
    if (attempt >= RETRY_DELAYS_MS.length) throw error;
    await wait(RETRY_DELAYS_MS[attempt]);
    return loadWithRetry(load, attempt + 1);
  }
};

/**
 * `React.lazy` for a route component, with retry on a failed chunk fetch.
 *
 * Use for any route whose code should not be part of the first visit.
 * Routes that must render without a second network round trip — the login
 * screen an unauthenticated visitor lands on, the 404 — stay statically
 * imported in `routes.tsx`.
 */
export const lazyRoute = <T extends ComponentType<any>>(load: Loader<T>) =>
  lazy(() => loadWithRetry(load));

export default lazyRoute;
